import React, { useContext, useRef, useState, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { Help } from '@atlaskit/navigation-system/top-nav-items';

import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useInProductHelpActions } from '@confluence/in-product-help/entry-points/InProductHelpProvider';
import { getRouteData } from '@confluence/in-product-help/entry-points/inProductHelpUtil';
import {
	HELP_PANEL_ID,
	HELP_PANEL_WIDTH,
} from '@confluence/in-product-help/entry-points/constants';
import { START_TOUCH } from '@confluence/navdex/entry-points/NavdexPointType';
import { VIEW_IN_PRODUCT_HELP_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener';
import {
	HELP_SHORTCUT,
	UNIVERSAL_HELP_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts';
import { KeyboardShortcutsDialog } from '@confluence/keyboard-shortcuts-dialog';
import { preloadHelpMenu } from '@confluence/nav-menus/entry-points/preloadHelpMenu';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage';

export const HelpButton = () => {
	const triggerButtonRef = useRef<HTMLButtonElement | null>(null);
	const rightSidebarContext = useRightSidebarContext();
	const inProductHelp = useInProductHelpActions();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const routeData = useRouteDataRef();
	const intl = useIntl();
	const isHelpPanelOpen = rightSidebarContext.getCurrent()?.panelId === HELP_PANEL_ID;
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();

	const [activeDialog, setActiveDialog] = useState(null);

	const onClick = () => {
		if (isHelpPanelOpen) {
			rightSidebarContext.close(HELP_PANEL_ID);
		} else {
			experienceTracker.start({
				name: VIEW_IN_PRODUCT_HELP_EXPERIENCE,
				attributes: {
					navVersion: '4',
				},
			});

			rightSidebarContext.open(HELP_PANEL_ID, HELP_PANEL_WIDTH);
			rightSidebarContext.setTriggerButtonRef?.(triggerButtonRef);

			const { routeName, routeGroup } = getRouteData(routeData.current?.href || '');
			inProductHelp.setRouteName(routeName);
			inProductHelp.setRouteGroup(routeGroup);
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'globalNavigation',
				action: 'clicked',
				actionSubject: 'navigationItem',
				actionSubjectId: 'help',
				attributes: {
					selectedItemPageContext: routeData.current?.routeName,
					navigationLayer: 'global',
					navigationContainer: 'top',
					navVersion: '4',
					navdexPointType: START_TOUCH,
				},
			},
		}).fire();
	};

	const closeDialog = useCallback(() => {
		setActiveDialog(null);
	}, [setActiveDialog]);

	const openDialog = useCallback(
		(dialog: any) => {
			setActiveDialog(dialog(closeDialog));
		},
		[setActiveDialog, closeDialog],
	);

	const openShortcutsDialog = () =>
		openDialog((onClose) => <KeyboardShortcutsDialog onClose={onClose} />);

	const preloadMenu = () => {
		void preloadHelpMenu();
	};

	return (
		<>
			{/* eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events */}
			<div onMouseEnter={preloadMenu}>
				<Help
					onClick={onClick}
					label={intl.formatMessage(i18n.help)}
					isSelected={isHelpPanelOpen}
					ref={triggerButtonRef}
				/>
			</div>
			<GeneralShortcutListener
				accelerator={
					isLiveEditMode || isOnEditRoute
						? UNIVERSAL_HELP_SHORTCUT
						: [HELP_SHORTCUT, UNIVERSAL_HELP_SHORTCUT]
				}
				listener={openShortcutsDialog}
				isAppNavigationShortcut
			/>
			<ModalTransition>{activeDialog}</ModalTransition>
		</>
	);
};

const i18n = defineMessages({
	help: {
		id: 'app-navigation.top-navigation.help',
		defaultMessage: 'Help',
		description: 'Label for the top navigation help button',
	},
});
